import {
  Box,
  BoxProps,
  Divider,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  VStack,
} from '@chakra-ui/react'
import {
  Assessment,
  ASSESSMENTS,
  colors,
  CoverageId,
  FieldMap,
  FieldMapValue,
  getHasPrimaryCoverage,
  getHasSecondaryCoverage,
  getInsuranceCoverageField,
  getInsuranceInfoStage,
  getInsurancePlan,
  getPregnancyPrimaryCoverageField,
  getQuestionnaireDeliveryStage,
  InsuranceCoverage,
  insuranceInfoStage,
  makeAllFieldsOptional,
  midwifeOnlyDeliveryStage,
  PopulatedUser,
  ShareCorrections,
  UpdateCallback,
} from '@hb/shared'

import { PopUpMessageContext, useApp } from '@hb/react'
import { FORM_ERROR, ValidationErrors } from 'final-form'
import { doc, DocumentReference, updateDoc } from 'firebase/firestore'
import { set as nestedSet } from 'nested-property'
import { useCallback, useContext, useMemo } from 'react'
import { ASSESSMENTS_REF } from '../../../backend/db/collections'
import { useProfile } from '../../../contexts/user/ProfileContext'
import { useUpdateAssessmentAnswers } from '../../../utils/assessments/updateAssessmentAnswers'
import { DataView } from '../../DataView'
import { useSubmitCoverage } from '../Coverage/hooks'

const sectionBoxProps: BoxProps = {
  borderBottom: '1px solid #cdcdcd',
  bg: 'white',
  borderRadius: 4,
  boxShadow: 'md',
}

const getQuestionnaireSectionPath = (
  id: 'delivery' | 'insuranceInfo' | 'introduction' | 'callIn',
) => {
  switch (id) {
    case 'delivery':
      return 'delivery'
    case 'callIn':
    case 'insuranceInfo':
      return 'insuranceInfo'
    default:
      return null
  }
}

interface UseInsuranceInfoSection {
  coverageData: FieldMapValue
  coverageCorrections: FieldMapValue
  coverageField: FieldMap | null
  handleSubmit: (data: FieldMapValue) => Promise<ValidationErrors>
}

const getPregnancyCoveragePath = (
  coverage: InsuranceCoverage,
  assessmentData: Assessment | null,
) => {
  const { data: pregnancyData, corrections: pregnancyCorrections } = assessmentData ?? {}
  const pregnancyPrimaryCoverageInsurerId =
    pregnancyCorrections?.insuranceInfo?.primaryCoverage?.insuranceProviderId ||
    pregnancyData?.insuranceInfo?.primaryCoverage?.insuranceProviderId
  const pregnancyPrimaryMemberId =
    pregnancyCorrections?.insuranceInfo?.primaryCoverage?.memberId ||
    pregnancyData?.insuranceInfo?.primaryCoverage?.memberId
  if (
    coverage.insuranceProviderId === pregnancyPrimaryCoverageInsurerId &&
    coverage.memberId === pregnancyPrimaryMemberId
  ) {
    return 'primaryCoverage'
  }
  const pregnancySecondaryCoverageInsurerId =
    pregnancyCorrections?.insuranceInfo?.secondaryCoverage?.insuranceProviderId ||
    pregnancyData?.insuranceInfo?.secondaryCoverage?.insuranceProviderId
  const pregnancySecondaryMemberId =
    pregnancyCorrections?.insuranceInfo?.secondaryCoverage?.memberId ||
    pregnancyData?.insuranceInfo?.secondaryCoverage?.memberId
  if (
    coverage.insuranceProviderId === pregnancySecondaryCoverageInsurerId &&
    coverage.memberId === pregnancySecondaryMemberId
  ) {
    return 'secondaryCoverage'
  }
  return null
}

const getCoverageData = (
  coverageId: CoverageId,
  assessmentData: Assessment | null,
  user: PopulatedUser | null,
) => {
  const { data: pregnancyData, corrections: pregnancyCorrections } = assessmentData ?? {}
  if (coverageId === 'primary') {
    if (assessmentData?.primaryCoverageId) {
      const userPlan = getInsurancePlan(
        user?.insurancePlans ?? {},
        assessmentData.primaryCoverageId,
      )
      if (userPlan) {
        return {
          coverageExists: true,
          coverageData: userPlan,
          coverageCorrections: undefined,
          pregnancyCoveragePath: getPregnancyCoveragePath(userPlan, assessmentData),
        }
      }
    }
    return {
      coverageExists: false,
      coverageData: pregnancyData?.insuranceInfo?.primaryCoverage,
      coverageCorrections: pregnancyCorrections?.insuranceInfo?.primaryCoverage,
      pregnancyCoveragePath: 'primaryCoverage',
    }
  } else {
    if (assessmentData?.secondaryCoverageId) {
      const userPlan = getInsurancePlan(
        user?.insurancePlans ?? {},
        assessmentData.secondaryCoverageId,
      )
      if (userPlan) {
        return {
          coverageExists: true,
          coverageData: userPlan,
          coverageCorrections: undefined,
          pregnancyCoveragePath: getPregnancyCoveragePath(userPlan, assessmentData),
        }
      }
    }
    return {
      coverageExists: false,
      coverageData: pregnancyData?.insuranceInfo?.secondaryCoverage,
      coverageCorrections: pregnancyCorrections?.insuranceInfo?.secondaryCoverage,
      pregnancyCoveragePath: 'secondaryCoverage',
    }
  }
}

const useInsuranceInfoSection = (pregnancyCoverageId: CoverageId): UseInsuranceInfoSection => {
  const {
    user,
    selectedAssessment: { assessmentData, populated },
    assessmentId,
    patientRef,
  } = useProfile()
  const { mergedData } = populated ?? {}
  const { coverageData, coverageCorrections, coverageExists } = useMemo(
    () => getCoverageData(pregnancyCoverageId, assessmentData, user),
    [pregnancyCoverageId, assessmentData, user],
  )

  const coverageId = useMemo(() => {
    if (pregnancyCoverageId === 'primary') return assessmentData?.primaryCoverageId ?? 'primary'
    return assessmentData?.secondaryCoverageId ?? 'secondary'
  }, [pregnancyCoverageId, assessmentData])

  const coverageField = useMemo(() => {
    const hasPrimaryCoverage = getHasPrimaryCoverage(mergedData?.insuranceInfo ?? {})
    const hasSecondaryCoverage = getHasSecondaryCoverage(mergedData?.insuranceInfo)
    const hasCoverage =
      pregnancyCoverageId === 'primary' ? hasPrimaryCoverage : hasSecondaryCoverage
    if (!hasCoverage) return null
    if (!coverageExists)
      return getPregnancyPrimaryCoverageField(mergedData?.insuranceInfo, true, true)
    return getInsuranceCoverageField(
      coverageCorrections?.isMedicaid ?? coverageData?.isMedicaid,
      true,
      true,
      true,
    )
  }, [coverageData, mergedData, coverageExists, coverageCorrections, pregnancyCoverageId])

  const submitCoverage = useSubmitCoverage(
    patientRef,
    coverageId,
    coverageData,
    assessmentId,
    populated,
    pregnancyCoverageId,
    null,
  )

  // const handleSubmit = useCallback(
  //   async (data: FieldMapValue) => {
  //     if (!onSubmitQuestionnaireCoverage) return { [FORM_ERROR]: 'An error occurred' }
  //     try {
  //       if (pregnancyCoveragePath) {
  //         const { error } = await onSubmitQuestionnaireCoverage(pregnancyCoveragePath, data)
  //         if (error) return { [FORM_ERROR]: error }
  //         return undefined
  //       } else {
  //         await
  //       }
  //     } catch (err: any) {
  //       return { [FORM_ERROR]: err.message || 'An error occurred' }
  //     }
  //   },
  //   [onSubmitQuestionnaireCoverage],
  // )

  return {
    coverageData,
    coverageCorrections,
    coverageField,
    handleSubmit: submitCoverage,
  }
}

const useQuestionnaireSection = (
  id: 'delivery' | 'insuranceInfo',
  baseStoragePath: string,
  assessmentRef: DocumentReference<Assessment>,
  handleSave: (id: 'delivery' | 'insuranceInfo', updated: FieldMapValue) => Promise<UpdateCallback>,
) => {
  const {
    selectedAssessment: { populated: assessment },
  } = useProfile()
  const { appName } = useApp()
  const storagePath = useMemo(() => `${baseStoragePath}/${id}`, [baseStoragePath, id])

  const handleShareCorrections = useCallback(
    async (path: string | null, shared: ShareCorrections | null) =>
      updateDoc(
        assessmentRef,
        `corrections.${id}.${path ? `${path}.` : ''}correctionsShared`,
        shared,
      ),
    [assessmentRef, id],
  )

  const onShareCorrections = useMemo(
    () => (appName === 'app' ? handleShareCorrections : undefined),
    [appName, handleShareCorrections],
  )

  const handleSubmit = useCallback(
    (updated: FieldMapValue) => handleSave(id, updated),
    [handleSave, id],
  )

  const canSubmit = useMemo(() => {
    if (appName === 'app') return true
    return id === 'delivery' || !assessment?.submittedOn
  }, [appName, assessment, id])

  const onSubmit = useMemo(() => (canSubmit ? handleSubmit : undefined), [canSubmit, handleSubmit])

  const handleUpdateField = useCallback(
    (path: string, value: FieldMapValue) => {
      const updated = {}
      nestedSet(updated, path, value)
      return handleSave(id, updated)
    },
    [handleSave, id],
  )

  const onUpdateField = useMemo(
    () => (canSubmit ? handleUpdateField : undefined),
    [canSubmit, handleUpdateField],
  )

  return {
    storagePath,
    onUpdateField,
    onSubmit,
    onShareCorrections,
  }
}

const PregnancyCoverageSectionView = ({
  data,
  field,
  storagePath,
}: {
  data: UseInsuranceInfoSection
  field: FieldMap
  storagePath: string
}) => {
  const { coverageData, coverageCorrections, handleSubmit } = data

  // should update pregnancy questionnaire if coverage matches pregnancy data
  // otherwise update plan on patient

  const handleUpdateField = useCallback(
    async (path: string, value: FieldMapValue) => {
      const updated = {}
      nestedSet(updated, path, value)
      const res = await handleSubmit(updated)
      if (res?.[FORM_ERROR]) return { error: res[FORM_ERROR] }
      if (res) return { error: 'An error occurred' }
      return { success: 'Updated!' }
    },
    [handleSubmit],
  )

  return (
    <DataView
      alwaysExpanded
      field={field}
      data={coverageData}
      corrections={coverageCorrections}
      baseStoragePath={storagePath}
      onSubmit={handleSubmit}
      updateField={handleUpdateField}
    />
  )
}

const InsuranceInfoSectionView = ({
  handleSave,
  baseStoragePath,
  assessmentRef,
}: {
  handleSave: (id: 'insuranceInfo' | 'delivery', updated: FieldMapValue) => Promise<UpdateCallback>
  baseStoragePath: string
  assessmentRef: DocumentReference<Assessment>
}) => {
  const {
    pregnancyCorrections,
    pregnancyData,
    selectedAssessment: { populated },
  } = useProfile()

  const { questionnaireData, questionnaireCorrections } = useMemo(() => {
    const {
      primaryCoverage: primaryCoverageData,
      secondaryCoverage: secondaryCoverageData,
      ...insuranceInfoData
    } = pregnancyData?.insuranceInfo ?? {}
    const {
      primaryCoverage: primaryCoverageCorrections,
      secondaryCoverage: secondaryCoverageCorrections,
      ...insuranceInfoCorrections
    } = pregnancyCorrections?.insuranceInfo ?? {}
    return {
      questionnaireData: {
        insuranceInfoData,
        primaryCoverage: primaryCoverageData,
        secondaryCoverage: secondaryCoverageData,
      },
      questionnaireCorrections: {
        insuranceInfoCorrections,
        primaryCoverage: primaryCoverageCorrections,
        secondaryCoverage: secondaryCoverageCorrections,
      },
    }
  }, [pregnancyData, pregnancyCorrections])

  const modalStage = useMemo(
    () =>
      makeAllFieldsOptional(
        getInsuranceInfoStage(populated?.mergedData?.insuranceInfo, true, true),
      ),
    [populated],
  )

  const { storagePath, onUpdateField, onSubmit, onShareCorrections } = useQuestionnaireSection(
    'insuranceInfo',
    baseStoragePath,
    assessmentRef,
    handleSave,
  )
  // const handleUpdateCoverage = useCallback(
  //   (coverageId: 'primary' | 'secondary', data: FieldMapValue) => {
  //     if (!onUpdateField) throw new Error('No onUpdateField function provided')
  //     return onUpdateField(coverageId === 'primary' ? 'primaryCoverage' : 'secondaryCoverage', data)
  //   },
  //   [onUpdateField],
  // )
  const primarySection = useInsuranceInfoSection('primary')
  const secondarySection = useInsuranceInfoSection('secondary')

  let coverageBody: React.ReactNode = null
  if (primarySection.coverageField) {
    if (secondarySection.coverageField) {
      coverageBody = (
        <Tabs borderTop={`1px solid ${colors.green.hex}77`} bg="white" colorScheme="green">
          <TabList>
            {primarySection.coverageField ? <Tab py={1.5}>Primary Coverage</Tab> : null}
            {secondarySection.coverageField ? <Tab py={1.5}>Secondary Coverage</Tab> : null}
          </TabList>
          <TabPanels minH="400px">
            {primarySection.coverageField ? (
              <TabPanel p={0}>
                <PregnancyCoverageSectionView
                  data={primarySection}
                  storagePath={`${storagePath}/primaryCoverage`}
                  field={primarySection.coverageField}
                />
              </TabPanel>
            ) : null}
            {secondarySection.coverageField ? (
              <TabPanel p={0}>
                <PregnancyCoverageSectionView
                  data={secondarySection}
                  storagePath={`${storagePath}/secondaryCoverage`}
                  field={secondarySection.coverageField}
                />
              </TabPanel>
            ) : null}
          </TabPanels>
        </Tabs>
      )
    } else {
      coverageBody = (
        <PregnancyCoverageSectionView
          data={primarySection}
          storagePath={`${storagePath}/primaryCoverage`}
          field={primarySection.coverageField}
        />
      )
    }
  }

  return (
    <Box shadow="md" borderRadius={4} w="100%">
      <DataView
        field={insuranceInfoStage}
        withEditModal={!!onSubmit}
        baseStoragePath={storagePath}
        alwaysExpanded
        onShareCorrections={onShareCorrections}
        adminView
        data={questionnaireData.insuranceInfoData}
        corrections={questionnaireCorrections.insuranceInfoCorrections}
        editModalField={modalStage}
        editModalData={pregnancyData?.insuranceInfo}
        editModalCorrections={pregnancyCorrections?.insuranceInfo}
        onSubmit={onSubmit}
        updateField={onUpdateField}
      />
      {coverageBody}
    </Box>
  )
}

const QuestionnaireSectionView = ({
  id,
  baseStoragePath,
  fields,
  assessmentRef,
  handleSave,
}: {
  id: 'delivery' | 'insuranceInfo'
  baseStoragePath: string
  fields: FieldMap
  assessmentRef: DocumentReference<Assessment>
  handleSave: (id: 'delivery' | 'insuranceInfo', updated: FieldMapValue) => Promise<UpdateCallback>
}) => {
  const { pregnancyCorrections, pregnancyData } = useProfile()

  const data = useMemo(() => {
    switch (id) {
      case 'delivery':
        return pregnancyData?.delivery
      case 'insuranceInfo':
        return pregnancyData?.insuranceInfo
      default:
        return null
    }
  }, [id, pregnancyData])

  const corrections = useMemo(() => {
    switch (id) {
      case 'delivery':
        return pregnancyCorrections?.delivery
      case 'insuranceInfo':
        return pregnancyCorrections?.insuranceInfo
      default:
        return null
    }
  }, [id, pregnancyCorrections])

  const { storagePath, onUpdateField, onSubmit, onShareCorrections } = useQuestionnaireSection(
    id,
    baseStoragePath,
    assessmentRef,
    handleSave,
  )

  return (
    <DataView
      withEditModal={!!onSubmit}
      baseStoragePath={storagePath}
      boxProps={sectionBoxProps}
      field={fields}
      alwaysExpanded
      onShareCorrections={onShareCorrections}
      adminView
      data={data}
      corrections={corrections}
      onSubmit={onSubmit}
      updateField={onUpdateField}
    />
  )
}

export const AssessmentQuestionnaireView = ({ assessmentId }: { assessmentId: string }) => {
  const { appName } = useApp()
  const { pregnancyData, pregnancyCorrections } = useProfile()

  const { processResponse } = useContext(PopUpMessageContext)
  const baseStoragePath = useMemo(
    () => `${ASSESSMENTS}/${assessmentId}/corrections`,
    [assessmentId],
  )

  const updateAnswers = useUpdateAssessmentAnswers()
  const handleSave = useCallback(
    async (id: 'delivery' | 'insuranceInfo', updated: FieldMapValue) => {
      const path = getQuestionnaireSectionPath(id)
      const submitted = path ? {} : updated
      if (path) nestedSet(submitted, path, updated)
      return updateAnswers({ id: assessmentId, appName, data: submitted })
        .then(() => processResponse({ success: 'Updated!' }))
        .catch(err => processResponse({ error: err.message }))
    },
    [assessmentId, appName, processResponse, updateAnswers],
  )
  const assessmentRef = useMemo(() => doc(ASSESSMENTS_REF, assessmentId), [assessmentId])
  const deliveryStage = useMemo(() => {
    const option =
      pregnancyCorrections?.insuranceInfo?.option ?? pregnancyData?.insuranceInfo?.option
    if (option === 'not-pregnant') {
      const notPregnantOption =
        pregnancyCorrections?.insuranceInfo?.notPregnantOption ??
        pregnancyData?.insuranceInfo?.notPregnantOption
      if (notPregnantOption === 'current-plan') return midwifeOnlyDeliveryStage
    }
    return getQuestionnaireDeliveryStage(appName)
  }, [appName, pregnancyData, pregnancyCorrections])
  return (
    <VStack w="100%" spacing={2}>
      <QuestionnaireSectionView
        id="delivery"
        baseStoragePath={baseStoragePath}
        fields={deliveryStage}
        assessmentRef={assessmentRef}
        handleSave={handleSave}
      />
      <Box w="100%" bg="white">
        <Divider />
        {/* <QuestionnaireSectionView
          id="insuranceInfo"
          baseStoragePath={baseStoragePath}
          fields={insuranceInfoField}
          assessmentRef={assessmentRef}
          handleSave={handleSave}
        /> */}
        <InsuranceInfoSectionView
          handleSave={handleSave}
          baseStoragePath={baseStoragePath}
          assessmentRef={assessmentRef}
        />
      </Box>
    </VStack>
  )
}
